import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "src/store/reducers";
import { unlock, logout } from "src/store/Authentication/authActions";
import { AuthAPIService } from "src/api/Authentication/API_AuthService";
import { setAuthUser, removeAuthUser } from "src/services/Authentication/AuthenticationService";
import { removeStoreXUser } from "src/services/Stores/FilteredStoreService";
import logo from "../../assets/images/logo.png";

const SIDEBAR_DARK  = "#263238"; // sidebar background
const SIDEBAR_MID   = "#37474F"; // sidebar secondary
const BRAND_BLUE    = "#2196F3"; // primary / buttons

const SessionLockScreen: React.FC = () => {
  const auth = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpIsGoogle, setOtpIsGoogle] = useState(false);

  if (!auth.isLocked || !auth.user) return null;

  const { fullname, username } = auth.user;

  const initials = fullname
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(n => n[0])
    .join("")
    .toUpperCase();

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setLoading(true);
    setError(null);

    try {
      const response = await AuthAPIService.authenticate({
        username,
        password,
        OTP: otpRequired ? otp : null,
        isOTP: otpRequired,
        isEmail: otpRequired && !otpIsGoogle,
      });

      if (response.requiredOTP) {
        setOtpRequired(true);
        setOtpIsGoogle(response.googleOTP);
        setLoading(false);
        return;
      }

      const newState = { user: response, logged: true, isLocked: false };
      setAuthUser(newState);
      dispatch(unlock(response));
      setPassword("");
      setOtp("");
      setOtpRequired(false);
    } catch {
      setError("Contraseña incorrecta. Por favor intente de nuevo.");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleFullLogout = () => {
    removeAuthUser();
    removeStoreXUser();
    dispatch(logout());
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(26, 35, 42, 0.94)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          overflow: "hidden",
          padding: "40px 36px 32px",
          width: "100%",
          maxWidth: "420px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* Top accent bar — clipped by overflow:hidden del padre */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: BRAND_BLUE,
          }}
        />

        {/* Logo */}
        <div style={{ marginBottom: "24px" }}>
          <img src={logo} alt="logo" style={{ height: "36px", objectFit: "contain" }} />
        </div>

        {/* Lock icon badge */}
        <div
          style={{
            width: "52px",
            height: "52px",
            borderRadius: "50%",
            background: `${SIDEBAR_DARK}18`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 11H7V8C7 5.24 9.24 3 12 3C14.76 3 17 5.24 17 8V11Z"
              stroke={SIDEBAR_DARK}
              strokeWidth="1.8"
              fill="none"
            />
            <rect x="5" y="11" width="14" height="10" rx="2" fill={SIDEBAR_DARK} opacity="0.15" stroke={SIDEBAR_DARK} strokeWidth="1.8"/>
            <circle cx="12" cy="16" r="1.5" fill={SIDEBAR_DARK}/>
          </svg>
        </div>

        <h5
          style={{
            color: SIDEBAR_DARK,
            fontWeight: 700,
            fontSize: "18px",
            marginBottom: "4px",
          }}
        >
          Sesión Bloqueada
        </h5>
        <p
          style={{
            color: "#6c757d",
            fontSize: "13px",
            marginBottom: "24px",
          }}
        >
          Tu sesión expiró. Ingresa tu contraseña para continuar.
        </p>

        {/* User avatar */}
        <div
          style={{
            width: "68px",
            height: "68px",
            borderRadius: "50%",
            background: SIDEBAR_DARK,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
            fontWeight: 700,
            color: "#ffffff",
            marginBottom: "10px",
            boxShadow: `0 4px 14px ${SIDEBAR_DARK}55`,
          }}
        >
          {initials}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <p
            style={{
              fontWeight: 600,
              fontSize: "15px",
              color: "#212529",
              marginBottom: "2px",
            }}
          >
            {fullname}
          </p>
          <p style={{ fontSize: "13px", color: "#6c757d", marginBottom: 0 }}>
            {username}
          </p>
        </div>

        <form onSubmit={handleUnlock}>
          {!otpRequired ? (
            <div style={{ position: "relative", marginBottom: "16px", textAlign: "left" }}>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                placeholder="Contraseña"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                autoComplete="new-password"
                autoFocus
                style={{
                  paddingRight: "44px",
                  borderColor: error ? "#dc3545" : "#dee2e6",
                  borderRadius: "10px",
                  fontSize: "14px",
                  height: "44px",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: 0,
                  lineHeight: 1,
                }}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: "16px", textAlign: "left" }}>
              <label style={{ fontSize: "13px", color: "#495057", marginBottom: "6px", display: "block" }}>
                {otpIsGoogle ? "Código de Google Authenticator" : "Código enviado a tu correo"}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ingresa el código OTP"
                value={otp}
                onChange={e => setOtp(e.target.value)}
                autoFocus
                maxLength={6}
                style={{
                  borderRadius: "10px",
                  fontSize: "14px",
                  height: "44px",
                  letterSpacing: "6px",
                  textAlign: "center",
                }}
              />
            </div>
          )}

          {error && (
            <div
              style={{
                background: "#fff5f5",
                border: "1px solid #f5c6cb",
                borderRadius: "8px",
                padding: "10px 14px",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style={{ fontSize: "13px", color: "#721c24" }}>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (!password && !otpRequired) || (otpRequired && !otp)}
            style={{
              width: "100%",
              height: "44px",
              background: BRAND_BLUE,
              color: "#ffffff",
              border: "none",
              borderRadius: "10px",
              fontSize: "15px",
              fontWeight: 600,
              cursor: loading ? "wait" : "pointer",
              opacity: loading || (!password && !otpRequired) || (otpRequired && !otp) ? 0.65 : 1,
              transition: "opacity 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ width: "16px", height: "16px" }}
                />
                Verificando...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                Desbloquear sesión
              </>
            )}
          </button>
        </form>

        <div style={{ marginTop: "20px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
          <button
            onClick={handleFullLogout}
            style={{
              background: "none",
              border: "none",
              color: "#6c757d",
              fontSize: "13px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Cerrar sesión e iniciar con otra cuenta
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionLockScreen;
